import React, { useState } from 'react';

const AVATAR_COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#be185d'];
const avatarColor = (str) => AVATAR_COLORS[(str || '?').charCodeAt(0) % AVATAR_COLORS.length];
const initials = (nombres, apellidos) => {
  const n = (nombres || '').trim()[0] || '';
  const a = (apellidos || '').trim()[0] || '';
  return (n + a).toUpperCase() || '?';
};

const ESTADO_CITA = {
  pendiente: { label: 'Pendiente', color: '#f59e0b' },
  confirmada: { label: 'Confirmada', color: '#10b981' },
  completada: { label: 'Completada', color: '#6b7280' },
};

// Mismo criterio que esMensajeWA en CrmChatPanel.js: todo seguimiento que NO sea un
// mensaje de WhatsApp (entrante/saliente) es una nota (agregada desde "+ Seguimiento"
// o la pestaña "📝 Notas" del chat) — puede haber N de estas por prospecto.
const esNota = (s) => {
  const saliente = s.descripcion?.startsWith('📤 Enviado:');
  const entrante = s.tipo === 'whatsapp' && /^📱\s*(Primer contacto WA|Mensaje WA):/.test(s.descripcion || '');
  return !(saliente || entrante);
};

const formatFechaNota = (fecha) => new Date(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const seccion = (icono, titulo, children) => (
  <div style={{ marginBottom: '1.1rem' }}>
    <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.6rem' }}>
      {icono} {titulo}
    </div>
    {children}
  </div>
);

const CrmContactInfo = ({ prospecto, isMobile, etapas = [], onCambiarEtapa, onMarcarCliente, profesionales = [], onAsignarProfesional }) => {
  const [buscarProfesional, setBuscarProfesional] = useState('');
  const [showProfDropdown, setShowProfDropdown] = useState(false);

  if (isMobile || !prospecto) return null;

  const nombreCompleto = `${prospecto.nombres}${prospecto.apellidos ? ' ' + prospecto.apellidos : ''}`;
  const avatarBg = avatarColor(prospecto.nombres);
  const ini = initials(prospecto.nombres, prospecto.apellidos);

  // Match case-insensitive: prospectos viejos (antes de crm_etapas) tienen la etapa en
  // minúscula ('nuevo'), las etapas configurables usan nombres capitalizados ('Nuevo').
  const etapaActual = etapas.find(e => (e.nombre || '').toLowerCase() === (prospecto.etapa || '').toLowerCase());
  const colorEtapa = etapaActual?.color || '#475569';

  const profesionalActual = profesionales.find(p => p.id === prospecto.profesional_id);
  const nombreProfesionalActual = profesionalActual
    ? `${profesionalActual.nombres} ${profesionalActual.apellidos}`
    : (prospecto.profesional_nombre?.trim() || '');

  const camposContacto = [
    { label: 'Teléfono',   val: prospecto.telefono },
    { label: 'WhatsApp',   val: prospecto.whatsapp_number },
    { label: 'Email',      val: prospecto.email },
    { label: 'Empresa',    val: prospecto.empresa },
    { label: 'Cargo',      val: prospecto.cargo },
    { label: 'Cédula/RUC', val: prospecto.identificacion },
    { label: 'Ciudad',     val: prospecto.ciudad },
    { label: 'Dirección',  val: prospecto.direccion },
    { label: 'Asignado a', val: prospecto.asignado_nombre?.trim() || null },
  ].filter(({ val }) => val);

  const tareasPendientes = (prospecto.seguimientos || [])
    .filter(s => s.fecha_proximo && new Date(s.fecha_proximo) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.fecha_proximo) - new Date(b.fecha_proximo));

  const notas = (prospecto.seguimientos || [])
    .filter(esNota)
    .sort((a, b) => new Date(b.fecha_contacto) - new Date(a.fecha_contacto));

  return (
    <div style={{
      width: '300px', flexShrink: 0, borderLeft: '1px solid #e5e7eb',
      background: 'white', height: '100%', overflowY: 'auto', padding: '1.25rem 1.1rem',
    }}>
      {/* Avatar + nombre */}
      <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '50%', background: avatarBg, color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700',
          fontSize: '1.3rem', margin: '0 auto 0.6rem',
        }}>{ini}</div>
        <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#111827' }}>{nombreCompleto}</div>
        {prospecto.whatsapp_number && <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.15rem' }}>{prospecto.whatsapp_number}</div>}
      </div>

      {/* Tipo (cliente/prospecto) — seleccionable; pasar a Cliente dispara la misma
          conversión irreversible que el botón "Marcar como cliente" */}
      {seccion('🏷️', 'Tipo', (
        <div style={{ position: 'relative' }}>
          <select
            value={prospecto.es_cliente ? 'cliente' : 'prospecto'}
            disabled={prospecto.es_cliente}
            onChange={(e) => {
              if (e.target.value === 'cliente' && !prospecto.es_cliente) onMarcarCliente && onMarcarCliente();
            }}
            style={{
              width: '100%',
              background: prospecto.es_cliente ? '#d1fae5' : '#eff6ff',
              color: prospecto.es_cliente ? '#065f46' : '#1d4ed8',
              border: `1.5px solid ${prospecto.es_cliente ? '#6ee7b7' : '#bfdbfe'}`,
              borderRadius: '8px', padding: '0.5rem 2rem 0.5rem 0.75rem',
              fontSize: '0.82rem', fontWeight: '700',
              cursor: prospecto.es_cliente ? 'not-allowed' : 'pointer',
              appearance: 'none', WebkitAppearance: 'none',
            }}
            title={prospecto.es_cliente ? 'Ya es cliente — la conversión es irreversible' : 'Marcar como cliente'}
          >
            <option value="prospecto">🎯 Prospecto</option>
            <option value="cliente">✅ Cliente</option>
          </select>
          {!prospecto.es_cliente && (
            <span style={{
              position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none', color: '#1d4ed8', fontSize: '0.7rem',
            }}>▾</span>
          )}
        </div>
      ))}

      {/* Etapa del pipeline — combobox editable */}
      {etapas.length > 0 && seccion('📊', 'Etapa', (
        <div style={{ position: 'relative' }}>
          <select
            value={etapaActual?.nombre || prospecto.etapa || ''}
            onChange={(e) => onCambiarEtapa && onCambiarEtapa(prospecto.id, e.target.value, prospecto.etapa)}
            style={{
              width: '100%', background: colorEtapa + '14', color: colorEtapa,
              border: `1.5px solid ${colorEtapa}66`, borderRadius: '8px',
              padding: '0.5rem 2rem 0.5rem 0.75rem', fontSize: '0.82rem', fontWeight: '700',
              cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none',
            }}
            title="Cambiar etapa del prospecto"
          >
            {!etapaActual && prospecto.etapa && (
              <option value={prospecto.etapa}>{prospecto.etapa}</option>
            )}
            {etapas.map(e => (
              <option key={e.id} value={e.nombre}>{e.nombre}</option>
            ))}
          </select>
          <span style={{
            position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)',
            pointerEvents: 'none', color: colorEtapa, fontSize: '0.7rem',
          }}>▾</span>
        </div>
      ))}

      {/* Profesional asignado — combobox buscable */}
      {seccion('🧑‍⚕️', 'Profesional asignado', (
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={showProfDropdown ? buscarProfesional : nombreProfesionalActual}
            onChange={e => { setBuscarProfesional(e.target.value); setShowProfDropdown(true); }}
            onFocus={() => { setBuscarProfesional(''); setShowProfDropdown(true); }}
            onBlur={() => setTimeout(() => setShowProfDropdown(false), 150)}
            placeholder="Buscar profesional..."
            style={{
              width: '100%', border: '1.5px solid #e5e7eb', borderRadius: '8px',
              padding: '0.5rem 0.75rem', fontSize: '0.82rem', outline: 'none',
              boxSizing: 'border-box', color: '#1e293b',
            }}
          />
          {showProfDropdown && (() => {
            const termino = buscarProfesional.toLowerCase();
            const opciones = profesionales.filter(p => `${p.nombres} ${p.apellidos}`.toLowerCase().includes(termino));
            return (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                background: 'white', border: '1.5px solid #e5e7eb', borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto',
                marginTop: '2px',
              }}>
                {opciones.length === 0 ? (
                  <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: '#9ca3af' }}>Sin resultados</div>
                ) : opciones.map(p => (
                  <div key={p.id}
                    onMouseDown={() => {
                      onAsignarProfesional && onAsignarProfesional(prospecto.id, p.id, prospecto.profesional_id);
                      setShowProfDropdown(false);
                      setBuscarProfesional('');
                    }}
                    style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    {p.nombres} {p.apellidos}{p.especialidad ? ` · ${p.especialidad}` : ''}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      ))}

      {/* Datos de contacto */}
      {camposContacto.length > 0 && seccion('👤', 'Datos del cliente', (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {camposContacto.map(({ label, val }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.8rem' }}>
              <span style={{ color: '#94a3b8' }}>{label}</span>
              <span style={{ color: '#1e293b', fontWeight: '600', textAlign: 'right', wordBreak: 'break-word' }}>{val}</span>
            </div>
          ))}
        </div>
      ))}

      {/* Citas próximas */}
      {seccion('📅', 'Citas próximas', (
        !prospecto.citas_proximas?.length ? (
          <p style={{ fontSize: '0.78rem', color: '#cbd5e1', margin: 0 }}>Sin citas próximas</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {prospecto.citas_proximas.map(c => {
              const estado = ESTADO_CITA[c.estado] || { label: c.estado, color: '#6b7280' };
              const fecha = new Date(c.fecha_cita + 'T00:00:00').toLocaleDateString('es-EC', { day: '2-digit', month: 'short' });
              return (
                <div key={c.id} style={{ background: '#f8fafc', borderRadius: '8px', padding: '0.55rem 0.65rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#1e293b' }}>{fecha} · {c.hora_inicio?.slice(0, 5)}</span>
                    <span style={{ fontSize: '0.65rem', fontWeight: '700', color: estado.color, background: estado.color + '18', borderRadius: '999px', padding: '0.05rem 0.5rem' }}>{estado.label}</span>
                  </div>
                  {c.servicio_nombre && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.15rem' }}>{c.servicio_nombre}</div>}
                </div>
              );
            })}
          </div>
        )
      ))}

      {/* Tareas / seguimientos pendientes */}
      {tareasPendientes.length > 0 && seccion('⏰', 'Tareas y recordatorios', (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {tareasPendientes.map(t => (
            <div key={t.id} style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.5rem 0.65rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#b45309' }}>
                {new Date(t.fecha_proximo).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#78350f', marginTop: '0.1rem' }}>{t.descripcion}</div>
            </div>
          ))}
        </div>
      ))}

      {/* Notas — puede haber varias, se listan todas (más reciente primero) */}
      {(prospecto.notas || notas.length > 0) && seccion('📝', 'Notas', (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '260px', overflowY: 'auto' }}>
          {prospecto.notas && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.55rem 0.65rem' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.15rem' }}>General</div>
              <p style={{ fontSize: '0.8rem', color: '#374151', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{prospecto.notas}</p>
            </div>
          )}
          {notas.map(n => (
            <div key={n.id} style={{ background: '#fefce8', border: '1px solid #fef08a', borderRadius: '8px', padding: '0.55rem 0.65rem' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: '700', color: '#a16207', marginBottom: '0.15rem' }}>
                {n.usuario_nombre?.trim() || 'Nota'} · {formatFechaNota(n.fecha_contacto)}
              </div>
              <p style={{ fontSize: '0.8rem', color: '#713f12', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{n.descripcion}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default CrmContactInfo;
